import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { AdditionInfo } from '../store/contacts/contacts.types';
import { PartNumberDto } from '../store/partnumbers-list/partnumbers-list.types';
import { DatagridInfo } from './commit.model';
import { VendorCode } from './vendor-codes-rights.model';
import { hasAllowedFields } from './field-structure.utils';

export type PartNumbersFields = {
  /**
   * Required for commit form
   */
  controlType?: PartNumbersPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: PartNumbersPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: PartNumbersPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: PartNumbersPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: PartNumbersPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: PartNumbersPlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: PartNumbersPlaceHolderTypeVal;
};

export enum EnumPartNumbersViews {
  INFO = 0,
  ADDITIONS = 1,
  CONTACTS = 2,
  VENDORS = 3,
  BUYERS = 4,
  NOTIFICATION = 5,
  NOTES = 6,
  COMMENTS = 7,
  CONFIG = 8,
}

export enum EnumPartNumberInfoViews {
  LIST = 0,
  SINGLE_COLUMN = 1,
  MULTI_COLUMN = 2,
  RESPONSIVE_COLUMN = 3,
}

export enum EnumPNVendorViews {
  SIMPLE_TABLE = 0,
  GROUP_TABLE = 1,
}

export type PartNumbersPlaceHolderTypeVal = {
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
  nestedCreateAdditionForm?: boolean | number | string;
  nestedCreateBuyerForm?: boolean | number | string;
  nestedEditAdditionForm?: boolean | number | string;
  nestedEditBuyerForm?: boolean | number | string;
  nestedForm?: boolean | number | string; //use nested<component>form for specific Component
  nestedNotificationForm?: boolean | number | string;
};

export type PartNumberListFlags = {
  createdBy?: string;
  dateCreated: Date;
  dateModified: Date;
  flags: string[];
  id: string;
  key: string;
  modifiedBy: string;
  pnVc: VendorCode[];
};

export type PartNumberListContacts = {
  [key: string]: unknown;
  active?: boolean;
  additionInfos?: AdditionInfo[];
  addresses: Address[];
  adfsGuid?: string;
  alias?: string;
  contacts: Contacts[];
  createdBy?: string;
  dateCreated?: string;
  dateModified?: string;
  description?: string;
  firstName?: string;
  id: string;
  identityKey?: string;
  identityType?: string;
  lastName?: string;
  modifiedBy?: string;
  systemKeys?: SystemKeys[];
};

export type PartNumbersContacts = {
  contactType?: string;
  email?: string;
  firstName?: string;
  identityKey?: string;
  key?: string;
  lastName?: string;
  name?: string;
};

export type PartNumberTransactionDetails = {
  data?: PartNumberDto;
  datagridInfo?: DatagridInfo;
  id: string;
  type?: EnumPartNumberType;
};

export enum EnumPartNumberType {
  NEW = 0,
  EDIT = 1,
}

type Address = {
  addressType: string;
  city: string;
  country: string;
  postCode: string;
  state: string;
  street: string;
};

export type Contacts = {
  contacttype: string;
  value: string;
};

export type BuyersContacts = {
  contactType?: string;
  identityKey: string;
  partNumber: string;
  vendorCode: string;
};

export type DeleteBuyersContacts = {
  contactType?: string;
  id: string;
  identityKey: string;
  partNumber: string;
  vendorCode: string;
};

export enum EnumPartNumbersFieldStructure {
  PartNumberTable = 0,
  PartNumberCreateForm = 1,
  PartNumberEditForm = 2, //IF MORE FORM CHANGE NAME
  NestedTable = 3,
  NestedForm = 4,
  NestedBuyerTable = 5,
  NestedCreateBuyerForm = 6,
  NestedEditBuyerForm = 7,
  NestedContactsTable = 8,
  NestedNotificationTable = 9,
  NestedNotificationForm = 10,
  NestedAdditionTable = 11,
  NestedCreateAdditionForm = 12,
  NestedEditAdditionForm = 13,
}

export type SystemKeys = {
  key: string;
  system: string;
};

// export type PartNumbers = {
//   count: number;
//   result: PartNumbersDetail[];
// };

// export type PartNumbersDetail = {
//   awsPartNumber: string;
//   commodityCode: string;
//   description: string;
//   flags?: string[];
//   lastModifiedBy?: string;
//   lastModifiedDate?: string;
//   manufacturerPartNumber: string;
//   materialGroup: string;
//   mmPartNumber: string;
//   mrpController: string;
//   name: string;
//   partClass: string;
//   partNumber: string;
//   profiCenter: string;
//   vendorManufacturerPartNumber: string;
// };

export type PartNumberCode = {
  partNumber: string;
  vendorCodes: string[];
};

export class PartNumbersListField {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: PartNumbersFields,
    refId: keyof PartNumbersPlaceHolderTypeVal,
    actualId: keyof PartNumbersPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumPartNumbersFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumPartNumbersFieldStructure): MappedField[] {
    switch (type) {
      case EnumPartNumbersFieldStructure.PartNumberTable:
        return this.getDatagridFields();
      case EnumPartNumbersFieldStructure.PartNumberCreateForm:
        return this.getCreateFormList();
      case EnumPartNumbersFieldStructure.PartNumberEditForm:
        return this.getEditFormList();
      case EnumPartNumbersFieldStructure.NestedTable:
        return this.getPartNumberVendorFields();
      case EnumPartNumbersFieldStructure.NestedForm:
        return this.getPnFormFields();
      case EnumPartNumbersFieldStructure.NestedBuyerTable:
        return this.getPartNumberBuyerFields();
      case EnumPartNumbersFieldStructure.NestedCreateBuyerForm:
        return this.getBuyerFormFields();
      case EnumPartNumbersFieldStructure.NestedEditBuyerForm:
        return this.getEditBuyerFormFields();
      case EnumPartNumbersFieldStructure.NestedContactsTable:
        return this.getPartNumberContactsFields();
      case EnumPartNumbersFieldStructure.NestedNotificationTable:
        return this.getPartNumberNotificationFields();
      case EnumPartNumbersFieldStructure.NestedNotificationForm:
        return this.getNotificationFormFields();
      case EnumPartNumbersFieldStructure.NestedAdditionTable:
        return this.getPartNumberAdditionsFields();
      case EnumPartNumbersFieldStructure.NestedCreateAdditionForm:
        return this.getAdditionFormFields();
      case EnumPartNumbersFieldStructure.NestedEditAdditionForm:
        return this.getEditAdditionFormFields();
      default:
        return this.getDatagridFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: PartNumbersFields,
    refId: keyof PartNumbersPlaceHolderTypeVal,
    actualId: keyof PartNumbersPlaceHolderTypeVal,
  ): boolean => {
    if (this.userRightFields && !(field.isRequired && field.isRequired[actualId])) {
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: PartNumbersFields,
    refId: keyof PartNumbersPlaceHolderTypeVal,
    actualId: keyof PartNumbersPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getDatagridFields(): MappedField[] {
    return this.PartNumbersList.filter((field: PartNumbersFields): boolean => {
      return this.hasField(field, 'datagrid', 'datagrid');
    }).map((field: PartNumbersFields): MappedField => {
      return {
        field: (field.id?.datagrid as string) ?? '',
        headerText: (field.headerText?.datagrid as string) ?? '',
        type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
        visible: !!field.visible?.datagrid,
      };
    });
  }

  getCreateFormList(): MappedField[] {
    return this.PartNumbersList.filter((field: PartNumbersFields): boolean => {
      return this.hasField(field, 'datagrid', 'createForm');
    }).map((field: PartNumbersFields): MappedField => {
      return {
        controlType: field.controlType?.createForm ? (field.controlType.createForm as string) : '',
        field: (field.id?.datagrid as string) ?? '',
        headerText:
          (field.headerText?.createForm as string) ?? (field.headerText?.datagrid as string) ?? '',
        visible: !!field.visible?.datagrid,
      };
    });
  }

  getEditFormList(): MappedField[] {
    return this.PartNumbersList.filter((field: PartNumbersFields): boolean => {
      return this.hasField(field, 'datagrid', 'editForm');
    }).map((field: PartNumbersFields): MappedField => {
      return {
        controlType: field.controlType?.editForm ? (field.controlType.editForm as string) : '',
        field: (field.id?.datagrid as string) ?? '',
        headerText:
          (field.headerText?.editForm as string) ?? (field.headerText?.datagrid as string) ?? '',
        visible: !!field.visible?.datagrid,
      };
    });
  }

  public readonly PartNumbersList: PartNumbersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'PartNumber' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Description' },
      id: { datagrid: 'description' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Material Group' },
      id: { datagrid: 'materialGroup' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Commodity Code' },
      id: { datagrid: 'commodityCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Manufacturer PartNumber' },
      id: { datagrid: 'manufacturerPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'AWS PartNumber' },
      id: { datagrid: 'awsPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'MM PartNumber' },
      id: { datagrid: 'mmPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'MRP Controller' },
      id: { datagrid: 'mrpController' },
      visible: { datagrid: true },
    },
    // {
    //     id: { datagrid: 'lastModifiedBy' },
    //     headerText: { datagrid: 'Last Modified By' },
    //     // controlType: { editForm: 'simple' },
    //     dataType: { datagrid: 'string' },
    //     visible: { datagrid: true },
    // },
    // {
    //     id: { datagrid: 'lastModifiedDate' },
    //     headerText: { datagrid: 'Last Modified Date' },
    //     // controlType: { editForm: 'date' },
    //     dataType: { datagrid: 'dateUnchanged' },
    //     visible: { datagrid: true },
    // },
    {
      dataType: { datagrid: 'flag' },
      headerText: { datagrid: 'Flags' },
      id: { datagrid: 'flags' },
      visible: { datagrid: true },
    },
  ];

  getPartNumberVendorFields(): MappedField[] {
    return this.vendorPNs
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getPnFormFields(): MappedField[] {
    return this.vendorPNs
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedForm');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedForm
            ? (field.controlType.nestedForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.nestedForm as string) ??
            (field.headerText?.datagrid as string) ??
            '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly vendorPNs: PartNumbersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    // {
    //     id: { datagrid: 'partNumber' },
    //     headerText: { datagrid: 'Part Number' },
    //     controlType: { nestedForm: 'simple' },
    //     dataType: { datagrid:'string' },
    //     visible: { datagrid: true } ,
    // },
    {
      controlType: { nestedForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Section Name' },
      id: { datagrid: 'sectionName' },
      visible: { datagrid: true },
    },
    {
      // controlType: { nestedForm: 'flags' },
      dataType: { datagrid: 'flag' },
      headerText: { datagrid: 'Flags' },
      id: { datagrid: 'flags' },
      visible: { datagrid: true },
    },
  ];

  getPartNumberBuyerFields(): MappedField[] {
    return this.partnumberBuyers
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getBuyerFormFields(): MappedField[] {
    return this.partnumberBuyers
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedCreateBuyerForm');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedCreateBuyerForm
            ? (field.controlType.nestedCreateBuyerForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.nestedCreateBuyerForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getEditBuyerFormFields(): MappedField[] {
    return this.partnumberBuyers
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedEditBuyerForm');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedEditBuyerForm
            ? (field.controlType.nestedEditBuyerForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.nestedEditBuyerForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly partnumberBuyers: PartNumbersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    // {
    //   id: { datagrid: 'buyer' },
    //   headerText: { datagrid: 'Buyer' },
    //   dataType: { datagrid: 'string' },
    //   visible: { datagrid: true },
    // },
    {
      // controlType: { nestedCreateBuyerForm: 'd-partNumber' },
      // isRequired: { nestedCreateBuyerForm: true },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateBuyerForm: 'd-vendorCode', nestedEditBuyerForm: 'd-vendorCode' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      isRequired: { nestedCreateBuyerForm: true, nestedEditBuyerForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateBuyerForm: 'd-identityKey', nestedEditBuyerForm: 'd-identityKey' },
      dataType: { datagrid: 'string' },
      exclude: { datagrid: true },
      headerText: { datagrid: 'Identity Key' },
      id: { datagrid: 'identityKey' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateBuyerForm: 'dropDown', nestedEditBuyerForm: 'dropDown' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Contact Type' },
      id: { datagrid: 'contactType' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'First Name' },
      id: { datagrid: 'firstName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Last Name' },
      id: { datagrid: 'lastName' },
      visible: { datagrid: true },
    },
    // {
    //   id: { datagrid: 'phone' },
    //   headerText: { datagrid: 'Buyer Phone' },
    //   dataType: { datagrid: 'number' },
    //   visible: { datagrid: true },
    // },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email' },
      id: { datagrid: 'email' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateBuyerForm: 'date', nestedEditBuyerForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid From' },
      id: { datagrid: 'validFrom' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateBuyerForm: 'date', nestedEditBuyerForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid To' },
      id: { datagrid: 'validTo' },
      visible: { datagrid: true },
    },
  ];

  getPartNumberContactsFields(): MappedField[] {
    return this.vendorContacts
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly vendorContacts: PartNumbersFields[] = [
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'NAME' },
      id: { datagrid: 'firstName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'SURNAME' },
      id: { datagrid: 'lastName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'email' },
      headerText: { datagrid: 'EMAIL' },
      id: { datagrid: 'email' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'CONTACT TYPE' },
      id: { datagrid: 'contactType' },
      visible: { datagrid: true },
    },
  ];

  getPartNumberNotificationFields(): MappedField[] {
    return this.vendorNotification
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getNotificationFormFields(): MappedField[] {
    return this.vendorNotification
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedNotificationForm');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedNotificationForm
            ? (field.controlType.nestedNotificationForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly vendorNotification: PartNumbersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'email' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email To' },
      id: { datagrid: 'emailTo' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'email' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email To CC' },
      id: { datagrid: 'emailToCC' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'email' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email To BCC' },
      id: { datagrid: 'emailToBCC' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'text' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Description' },
      id: { datagrid: 'description' },
      visible: { datagrid: true },
    },
  ];

  getPartNumberAdditionsFields(): MappedField[] {
    return this.vendorAdditionFields
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getAdditionFormFields(): MappedField[] {
    return this.vendorAdditionFields
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedCreateAdditionForm');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedCreateAdditionForm
            ? (field.controlType.nestedCreateAdditionForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getEditAdditionFormFields(): MappedField[] {
    return this.vendorAdditionFields
      .filter((field: PartNumbersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedEditAdditionForm');
      })
      .map((field: PartNumbersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedEditAdditionForm
            ? (field.controlType.nestedEditAdditionForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly vendorAdditionFields: PartNumbersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: false },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple', nestedEditAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Property Name' },
      id: { datagrid: 'propertyName' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple', nestedEditAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Value' },
      id: { datagrid: 'value' },
      visible: { datagrid: true },
    },
  ];
}
